const CHUNK_LOAD_RECOVERY_ATTEMPTS_KEY = "chunk-load-recovery-attempts";
const MAX_STORED_ATTEMPTS = 5;

const CHUNK_LOAD_ERROR_PATTERNS = [
  /Loading (?:CSS )?chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

type RecoveryStorage = Pick<Storage, "getItem" | "setItem">;
type RecoveryServiceWorkerContainer = Pick<
  ServiceWorkerContainer,
  "getRegistration"
>;

interface ChunkLoadRecoveryOptions {
  storage?: RecoveryStorage;
  reload?: () => void;
  serviceWorker?: RecoveryServiceWorkerContainer | null;
}

export const isChunkLoadError = (error: Error): boolean => {
  const description = `${error.name}: ${error.message}`;
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(description));
};

const readPreviousAttempts = (storage: RecoveryStorage): string[] => {
  const storedAttempts = storage.getItem(CHUNK_LOAD_RECOVERY_ATTEMPTS_KEY);
  if (!storedAttempts) return [];

  const parsedAttempts: unknown = JSON.parse(storedAttempts);
  return Array.isArray(parsedAttempts)
    ? parsedAttempts.filter(
        (attempt): attempt is string => typeof attempt === "string"
      )
    : [];
};

const reloadWithoutStaleServiceWorkers = (
  reload: () => void,
  serviceWorker: RecoveryServiceWorkerContainer | null | undefined
): void => {
  if (!serviceWorker) {
    reload();
    return;
  }

  // A normal reload remains controlled by the current worker and can receive
  // the same stale app shell that referenced the missing chunk. Unregistering
  // first makes this reload equivalent to the hard refresh admins were using
  // manually. The fresh app registers the current worker again on startup.
  Promise.resolve()
    .then(() => serviceWorker.getRegistration())
    .then((registration) => registration?.unregister())
    .catch(() => undefined)
    .finally(reload);
};

/**
 * User-initiated hard reload that first drops any stale service worker, for
 * "Try Again" buttons on chunk-load failures. Unlike recoverFromChunkLoadError
 * this deliberately skips the once-per-signature session guard: a click is
 * explicit intent, so it cannot loop on its own.
 */
export const forceReloadWithoutStaleServiceWorkers = (
  options: Pick<ChunkLoadRecoveryOptions, "reload" | "serviceWorker"> = {}
): void => {
  const reload = options.reload ?? (() => window.location.reload());
  const serviceWorker =
    options.serviceWorker !== undefined
      ? options.serviceWorker
      : navigator.serviceWorker;
  reloadWithoutStaleServiceWorkers(reload, serviceWorker);
};

/**
 * Reload once for each failed chunk URL/signature in this tab. React.lazy
 * caches rejected imports, so retrying the route cannot recover without a page
 * load. Remembering the failure in session storage prevents a persistent 404
 * or offline connection from causing a reload loop.
 */
export const recoverFromChunkLoadError = (
  error: Error,
  options: ChunkLoadRecoveryOptions = {}
): boolean => {
  if (!isChunkLoadError(error)) return false;

  try {
    const storage = options.storage ?? window.sessionStorage;
    const reload = options.reload ?? (() => window.location.reload());
    const signature = `${error.name}: ${error.message}`;
    const previousAttempts = readPreviousAttempts(storage);

    if (previousAttempts.includes(signature)) return false;

    storage.setItem(
      CHUNK_LOAD_RECOVERY_ATTEMPTS_KEY,
      JSON.stringify(
        [...previousAttempts, signature].slice(-MAX_STORED_ATTEMPTS)
      )
    );
    const serviceWorker =
      options.serviceWorker !== undefined
        ? options.serviceWorker
        : navigator.serviceWorker;
    reloadWithoutStaleServiceWorkers(reload, serviceWorker);
    return true;
  } catch {
    // If storage is unavailable, do not reload: without the guard, a
    // persistent chunk failure could trap the user in a reload loop.
    return false;
  }
};
